import { personRef } from "@/content/author";
import { createFileRoute, Link } from "@tanstack/react-router";
import { hreflangLinks } from "@/lib/seo";
import { PageIntro, PageSection, MoreLink } from "@/components/site/page";

const URL = "https://akillisurdurulebilirlik.com/yazilar/surdurulebilir-kalkinma-nedir";
const TITLE = "Sürdürülebilir Kalkınma Nedir? Tanımı, Tarihi ve Eleştirel Bir Bakış";
const DESC =
  "Sürdürülebilir kalkınma nedir, Brundtland Raporu'ndan SDG'lere nasıl evrildi, sürdürülebilirlikten teknik olarak nasıl ayrılır? Kavramın tanımı, tarihi ve eleştirel bir okuması.";
const PUBLISHED = "2026-06-29";

const FAQ = [
  {
    q: "Sürdürülebilir kalkınma nedir, kısaca?",
    a: "Sürdürülebilir kalkınma (sustainable development), bugünün ihtiyaçlarını gelecek kuşakların kendi ihtiyaçlarını karşılama olanağını tehlikeye atmadan karşılamaya çalışan kalkınma biçimidir. Tanım 1987 Brundtland Raporu'nda formüle edildi ve üç ayağı vardır: ekonomik kalkınma, sosyal kapsayıcılık ve çevresel koruma.",
  },
  {
    q: "Sürdürülebilir kalkınma ile sürdürülebilirlik aynı şey mi?",
    a: "Hayır. Sürdürülebilirlik (sustainability) bir sistemin uzun vadede kendini sürdürebilme özelliğini tanımlayan daha geniş bir kavramdır; ekoloji, etik ve felsefe alanlarını da kapsar. Sürdürülebilir kalkınma ise bu çerçevenin politika diline tercüme edilmiş halidir — büyüme, yatırım ve kalkınma hedefleriyle birlikte düşünülür.",
  },
  {
    q: "Brundtland Raporu nedir?",
    a: "1987'de BM Dünya Çevre ve Kalkınma Komisyonu (başkanı Gro Harlem Brundtland) tarafından yayımlanan 'Ortak Geleceğimiz' (Our Common Future) raporudur. 'Sürdürülebilir kalkınma' kavramının uluslararası politika sözlüğüne girmesini sağlayan belge olarak kabul edilir.",
  },
  {
    q: "Sürdürülebilir kalkınmanın üç ayağı nedir?",
    a: "Üç ayak — yaygın adıyla 'üçlü sonuç' (triple bottom line): (1) ekonomik kalkınma, (2) sosyal eşitlik ve kapsayıcılık, (3) çevresel koruma. Modern yorumlarda dördüncü bir kültürel/kurumsal ayak da eklenir.",
  },
  {
    q: "SDG'ler ile sürdürülebilir kalkınma arasındaki ilişki nedir?",
    a: "SDG'ler (Sustainable Development Goals — Sürdürülebilir Kalkınma Amaçları), 2015'te BM tarafından kabul edilen, 2030'a kadar ulaşılması hedeflenen 17 küresel hedeftir. Sürdürülebilir kalkınma soyut bir çerçeve; SDG'ler bu çerçevenin ölçülebilir hedeflere indirgenmiş operasyonel versiyonudur.",
  },
  {
    q: "Sürdürülebilir kalkınma eleştirileri nelerdir?",
    a: "Üç ana eleştiri vardır: (1) İçsel çelişki — sınırsız büyümeyi sınırlı bir gezegende sürdürmeyi varsayar. (2) Belirsizlik — 'ihtiyaç' ve 'gelecek kuşak' tanımları siyasal olarak boştur. (3) Güç asimetrisi — kalkınma dilini koruyarak, mevcut ekonomik düzeni dönüştürmek yerine yeşilleyen ('greenwashing') bir araca dönüşebilir.",
  },
];

export const Route = createFileRoute("/yazilar/surdurulebilir-kalkinma-nedir")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      {
        name: "keywords",
        content:
          "sürdürülebilir kalkınma, sürdürülebilir kalkınma nedir, sustainable development, brundtland raporu, sürdürülebilir kalkınma tanımı, sürdürülebilir kalkınma tarihi, sürdürülebilirlik, sdg, üçlü sonuç, triple bottom line",
      },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [
      { rel: "canonical", href: URL },
      ...hreflangLinks("/yazilar/surdurulebilir-kalkinma-nedir"),
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: TITLE,
          description: DESC,
          inLanguage: "tr-TR",
          datePublished: PUBLISHED,
          dateModified: PUBLISHED,
          author: personRef(),
          publisher: {
            "@type": "Organization",
            name: "Akıllı Sürdürülebilirlik",
            url: "https://akillisurdurulebilirlik.com",
          },
          mainEntityOfPage: { "@type": "WebPage", "@id": URL },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Ana Sayfa", item: "https://akillisurdurulebilirlik.com/" },
            { "@type": "ListItem", position: 2, name: "Yazılar", item: "https://akillisurdurulebilirlik.com/yazilar" },
            { "@type": "ListItem", position: 3, name: "Sürdürülebilir Kalkınma Nedir", item: URL },
          ],
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <PageIntro
        crumbs={[{ label: "Yazılar", to: "/yazilar" }, { label: "Sürdürülebilir Kalkınma Nedir" }]}
        kicker="Temel Kavram Rehberi"
        title="Sürdürülebilir kalkınma nedir?"
        lede={
          <>
            Brundtland Raporu'ndan SDG'lere uzanan bir kavramın tanımı, tarihi ve
          neden hâlâ tartışmalı olduğunun eleştirel okuması.
          </>
        }
      />
      <PageSection>
        <article className="max-w-3xl">

      <nav className="mb-12 border border-ink/10 p-5 md:p-6 bg-secondary" aria-label="İçindekiler">
        <h2 className="text-[10px] uppercase tracking-[0.25em] font-bold text-ink/60 mb-3">İçindekiler</h2>
        <ol className="space-y-1.5 text-sm">
          <li><a href="#tanim" className="hover:text-theme-tech">1. Tanım: Brundtland'den bugüne</a></li>
          <li><a href="#tarih" className="hover:text-theme-tech">2. Kısa tarih: Stockholm'den Agenda 2030'a</a></li>
          <li><a href="#uc-ayak" className="hover:text-theme-tech">3. Üç ayak: ekonomi, toplum, çevre</a></li>
          <li><a href="#fark" className="hover:text-theme-tech">4. Sürdürülebilirlik ile farkı</a></li>
          <li><a href="#sdg" className="hover:text-theme-tech">5. SDG'lere giden yol</a></li>
          <li><a href="#elestiri" className="hover:text-theme-tech">6. Eleştirel okuma</a></li>
          <li><a href="#sss" className="hover:text-theme-tech">7. Sıkça sorulanlar</a></li>
        </ol>
      </nav>

      <div className="prose-content space-y-6 text-base md:text-lg leading-[1.75] text-ink/85">
        <section id="tanim" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            1. Tanım: Brundtland'den bugüne
          </h2>
          <p>
            <strong>Sürdürülebilir kalkınma</strong> (sustainable development),
            uluslararası politika sözlüğüne 1987'de{" "}
            <em>Ortak Geleceğimiz</em> (Our Common Future) — bilinen adıyla{" "}
            <strong>Brundtland Raporu</strong> — ile girdi. Raporun ünlü
            tanımı şöyledir:
          </p>
          <blockquote className="border-l-2 border-clay pl-5 italic text-ink/75">
            "Bugünün ihtiyaçlarını, gelecek kuşakların kendi ihtiyaçlarını
            karşılama olanağını tehlikeye atmadan karşılayan kalkınma."
          </blockquote>
          <p>
            Tanım iki düzeyde çalışır. Birinci düzey teknik: bir nesil
            tükettiğinden fazlasını harcamaz. İkinci düzey etiktir: gelecek
            kuşakların hakkını şimdiden hesaba katmayan bir kalkınma, kalkınma
            sayılamaz. Bu ikinci yan, kavramı sıradan bir büyüme hedefinden
            ayıran şeydir.
          </p>
        </section>

        <section id="tarih" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            2. Kısa tarih: Stockholm'den Agenda 2030'a
          </h2>
          <p>
            Kavramın kökleri Brundtland'den eskiye gider. 1972'de{" "}
            <strong>Stockholm Konferansı</strong> çevre ile kalkınma arasındaki
            gerilimi ilk kez küresel bir gündem yaptı. Aynı yıl Roma Kulübü'nün{" "}
            <em>Büyümenin Sınırları</em> (Limits to Growth) raporu, sınırsız
            büyüme paradigmasının matematiksel olarak sürdürülemez olduğunu
            ileri sürdü.
          </p>
          <p>
            1987 Brundtland Raporu bu iki damarı birleştirdi. 1992{" "}
            <strong>Rio Zirvesi</strong> kavramı Agenda 21 ile somutlaştırdı.
            2000'de <strong>Binyıl Kalkınma Hedefleri (MDGs)</strong>, 2015'te
            ise <strong>SDG'ler</strong> — 17 hedef, 169 alt hedef — ile
            sürdürülebilir kalkınma operasyonel bir çerçeveye dönüştü.
          </p>
        </section>

        <section id="uc-ayak" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            3. Üç ayak: ekonomi, toplum, çevre
          </h2>
          <p>
            Sürdürülebilir kalkınma standart anlatımda üç ayak üzerinde
            durur — literatürde <strong>triple bottom line</strong> olarak da
            geçer:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Ekonomik kalkınma:</strong> üretkenlik, istihdam, refah.</li>
            <li><strong>Sosyal eşitlik:</strong> yoksullukla mücadele, eğitim, sağlık, kapsayıcılık.</li>
            <li><strong>Çevresel koruma:</strong> iklim, biyoçeşitlik, kaynak yönetimi.</li>
          </ul>
          <p>
            Modern yorumlarda dördüncü bir <strong>kültürel/kurumsal</strong>{" "}
            ayak da eklenir: kurumlar, demokratik yönetişim ve kültürel
            sürdürülebilirlik. Üç ayağı eşit önemde görmek teorik bir
            iddiadır; pratikte hemen her ülkede ekonomik ayak diğer ikisini
            ezer.
          </p>
        </section>

        <section id="fark" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            4. Sürdürülebilirlik ile farkı
          </h2>
          <p>
            İki kavram günlük dilde yer değiştirilebilir gibi kullanılır ama
            farklıdırlar.{" "}
            <Link to="/yazilar/$slug" params={{ slug: "surdurulebilirlik-nedir" }} className="text-theme-tech underline-offset-4 hover:underline">
              Sürdürülebilirlik
            </Link>
            , bir sistemin uzun vadede kendini sürdürebilme özelliğini tanımlar
            — ekoloji, etik ve felsefe alanlarına uzanır.{" "}
            <strong>Sürdürülebilir kalkınma</strong> ise bu çerçevenin{" "}
            <em>politika diline</em> tercüme edilmiş halidir: büyüme, yatırım
            ve kalkınma hedefleriyle birlikte düşünülür.
          </p>
          <p>
            Fark önemlidir, çünkü sürdürülebilirlik 'büyümemek' veya 'küçülmek'
            seçeneklerine açıkken, sürdürülebilir kalkınma daima bir kalkınma —
            yani büyüme — varsayar. Eleştirinin büyük kısmı bu varsayıma
            yöneliktir.
          </p>
        </section>

        <section id="sdg" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            5. SDG'lere giden yol
          </h2>
          <p>
            2015'te BM tarafından kabul edilen{" "}
            <strong>Sürdürülebilir Kalkınma Amaçları (SDG'ler)</strong>,
            soyut bir kalkınma anlayışını 17 hedef, 169 alt hedef ve 230'dan
            fazla göstergeyle ölçülebilir hale getirdi. SDG çerçevesi,
            sürdürülebilir kalkınma kavramının bugünkü en güçlü
            operasyonelleştirmesidir.
          </p>
          <p>
            17 hedefin nasıl çalıştığı, nerede tıkandığı ve 2030 takviminin
            neyi gösterdiği üzerine ayrıntılı eleştirel bir okuma için ayrı bir{" "}
            <Link to="/yazilar/sdg-rehberi" className="text-theme-tech underline-offset-4 hover:underline">
              SDG rehberi
            </Link>{" "}
            hazırladım. Aynı çerçevenin AB hukukundaki karşılığı olan{" "}
            <Link to="/yazilar/yesil-mutabakat-rehberi" className="text-theme-tech underline-offset-4 hover:underline">
              Avrupa Yeşil Mutabakatı rehberi
            </Link>{" "}
            de bu okumayı tamamlıyor.
          </p>
        </section>

        <section id="elestiri" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            6. Eleştirel okuma
          </h2>
          <p>Sürdürülebilir kalkınmaya yöneltilen üç temel eleştiri vardır:</p>
          <ol className="list-decimal pl-6 space-y-3">
            <li>
              <strong>İçsel çelişki.</strong> Sınırsız ekonomik büyümeyi sınırlı
              bir gezegende sürdürmeyi varsayar. 'Decoupling' tezi — büyümeyi
              kaynak tüketiminden ayırma — ampirik olarak büyük ölçüde
              doğrulanmadı.
            </li>
            <li>
              <strong>Tanım belirsizliği.</strong> 'İhtiyaç' kim tarafından
              tanımlanır? 'Gelecek kuşak' kaç nesil sonradır? Bu boşluklar,
              kavramı her aktör için uygun şekilde yeniden doldurulabilir hale
              getirir.
            </li>
            <li>
              <strong>Güç sorusu.</strong> Çerçeve 'ne' yapılacağını söyler ama
              'kim tarafından, kimin pahasına' sorusunu boş bırakır. Bu boşluk{" "}
              <Link to="/yazilar/greenwashing-nedir" className="text-theme-tech underline-offset-4 hover:underline">
                greenwashing
              </Link>{" "}
              ve SDG washing pratiklerine zemin hazırlar.
            </li>
          </ol>
          <p>
            Eleştiriler kavramı reddetmek için değil, ciddiye almak için
            verilir. Sürdürülebilir kalkınma, doğru sorulduğunda hâlâ küresel
            ölçekte üzerinde uzlaşılabilen birkaç çerçeveden biridir. Yanlış
            sorulduğunda ise yeşile boyanmış bir 'her şey aynen devam' söylemine
            indirgenir.
          </p>
        </section>

        <section id="sss" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            7. Sıkça sorulanlar
          </h2>
          <dl className="space-y-6">
            {FAQ.map((f) => (
              <div key={f.q}>
                <dt className="font-serif text-lg text-ink">{f.q}</dt>
                <dd className="mt-2 text-ink/75">{f.a}</dd>
              </div>
            ))}
          </dl>
        </section>
      </div>
        </article>
      </PageSection>
    </>
  );
}
